/**
 * Composable for accessing the TranslationService singleton.
 * Provides a single instance across the entire application.
 */
import { TranslationService } from '../translation/core/TranslationService';

let serviceInstance: TranslationService | null = null;

/**
 * Get or create the TranslationService singleton.
 * The service is lazily initialized on first call.
 *
 * @returns The shared TranslationService instance
 *
 * @example
 * ```typescript
 * const service = useTranslationService();
 * const results = await service.translate(requests);
 * ```
 */
export function useTranslationService(): TranslationService {
	if (!serviceInstance) {
		const apiKey = import.meta.env.VITE_GOOGLE_MAP_API_KEY;

		if (!apiKey) {
			console.warn('VITE_GOOGLE_MAP_API_KEY is not set. Translation will not work.');
		}

		serviceInstance = new TranslationService({
			apiKey: apiKey || '',
			chunkSize: 120,
			enableDetection: false,
		});
	}

	return serviceInstance;
}

/**
 * Reset the TranslationService singleton.
 * Useful for testing or when you need a fresh service instance.
 */
export function resetTranslationService(): void {
	if (serviceInstance) {
		serviceInstance.clearCache();
	}
	serviceInstance = null;
}

/**
 * Clear the translation cache without resetting the service.
 */
export function clearTranslationCache(): void {
	serviceInstance?.clearCache();
}
